/**
 * Domain types for the Real Time scheduling board.
 *
 * These are intentionally decoupled from the backend payloads in `@/types`. The API layer
 * (`api/realtime-api.ts`) maps backend responses into these shapes, and the mock provider
 * produces them directly.
 */

export type WorkerStatus = 'available' | 'on_job' | 'late' | 'offline'

export interface Worker {
  id: string
  name: string
  phone: string | null
  avatarUrl: string | null
  status: WorkerStatus
  lat: number | null
  lng: number | null
  /** ISO timestamp of the last location/heartbeat. */
  lastSeenAt: string | null
  currentJobId: string | null
  /** 0-5, optional because not every backend provides it. */
  rating?: number | null
  /**
   * Where lat/lng came from, in fallback order: live GPS -> last known ping -> base
   * (profile/shift start) location. null when no location is known.
   */
  locationSource?: WorkerLocationSource | null
  /** On approved leave for the selected day (never suggested as a nearby worker). */
  onLeave?: boolean
  /**
   * Job category ids the worker can do. `ANY_CATEGORY` ('*') = every category (a skill with no
   * category in the backend). Missing/empty = no category info, so the worker never matches a
   * job that has a category.
   */
  categories?: string[]
}

/** Wildcard entry in Worker.categories: the worker can do jobs in any category. */
export const ANY_CATEGORY = '*'

export type WorkerLocationSource = 'live' | 'last_known' | 'base'

/** The worker's answer to a job offer. */
export type WorkerResponse = 'pending' | 'accepted' | 'declined' | 'expired'

/** Progress of an assigned worker on the job itself. */
export type WorkerJobStatus = 'assigned' | 'checked_in' | 'on_the_way' | 'started' | 'completed'

export interface JobWorker {
  workerId: string
  response: WorkerResponse
  status: WorkerJobStatus
  /** When the offer was sent (ISO). */
  sentAt: string
  respondedAt?: string | null
  openedAt?: string | null
  lastReminderAt?: string | null
  declineReason?: string | null
  /** Backend handle for this worker's slot (e.g. JobAssignment id); opaque to the UI. */
  assignmentId?: string | null
}

export type JobStatus = 'unassigned' | 'scheduled' | 'en_route' | 'in_progress' | 'completed' | 'late'

export interface Job {
  id: string
  bookingNo: string
  service: string
  area: string
  lat: number | null
  lng: number | null
  /** ISO timestamps (UTC). Rendered in the branch timezone. */
  scheduledStart: string
  scheduledEnd: string
  status: JobStatus
  /** How many workers the job needs. */
  requiredWorkers: number
  /** Everyone offered/assigned to the job, including declined/expired offers. */
  workers: JobWorker[]
  /** ETA in minutes for an en-route worker, when the backend provides it. */
  etaMinutes?: number | null
  /** Full street address, used for display and on-demand geocoding when lat/lng are missing. */
  address?: string | null
  /** Job category id; nearby workers are filtered to this category. null = any category. */
  categoryId?: string | null
  /** Display name for the category ("Nearby Cleaning workers"). */
  categoryName?: string | null
  /**
   * The job is known (from Job/List) but its assignments have not loaded yet: who is on it is
   * unknown, so it is never counted as unassigned/late and its worker rows show a loading state.
   */
  staffingPending?: boolean
}

export type RealtimeEvent =
  | { type: 'worker.location.updated'; workerId: string; lat: number; lng: number; at: string }
  /** Alias of worker.location.updated used by the scheduling socket. */
  | { type: 'worker.location'; workerId: string; lat: number; lng: number; at: string }
  | { type: 'worker.status.changed'; workerId: string; status: WorkerStatus; currentJobId?: string | null; at: string }
  /** Alias of worker.status.changed used by the scheduling socket. */
  | { type: 'worker.status'; workerId: string; status: WorkerStatus; currentJobId?: string | null; at: string }
  | { type: 'job.created'; job: Job }
  | { type: 'job.updated'; job: Partial<Job> & { id: string } }
  | { type: 'job.deleted'; jobId: string }
  /** Legacy single-assignment event; treated as job.worker.added (pending). */
  | { type: 'job.assigned'; jobId: string; workerId: string; at: string }
  | { type: 'job.worker.added'; jobId: string; worker: JobWorker }
  | { type: 'job.worker.removed'; jobId: string; workerId: string; reason?: string | null }
  | { type: 'job.worker.response'; jobId: string; workerId: string; response: WorkerResponse; at: string; declineReason?: string | null }
  | { type: 'job.worker.status'; jobId: string; workerId: string; status: WorkerJobStatus; at: string }
  | { type: 'job.worker.reminded'; jobId: string; workerId: string; at: string }
  | { type: 'job.required.changed'; jobId: string; requiredWorkers: number }

export type RealtimeEventType = RealtimeEvent['type']

export interface WorkHours {
  /** Hour of day (0-23) in the branch timezone. */
  startHour: number
  /** Hour of day (1-24) in the branch timezone. */
  endHour: number
}

export interface RealtimeSnapshot {
  workers: Worker[]
  jobs: Job[]
  generatedAt: string
  /** IANA timezone of the branch, when the backend knows it. */
  timeZone?: string | null
  workHours?: WorkHours | null
  /** false while assignments are still loading (jobs may carry staffingPending). Default true. */
  staffingLoaded?: boolean
  /** Epoch ms when the assignment data in this snapshot was requested (optimistic-update guard). */
  staffingAsOf?: number
}

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'offline'

export type KpiFilter = 'on_job' | 'available' | 'unassigned' | 'late'

export type JobsTab = 'unassigned' | 'today' | 'late'

export type MapLayer = 'workers' | 'jobs' | 'routes'
